import React, { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { apiGet, apiPost, apiPut, apiDel } from '../lib/api.js';
import TopBar from '../components/TopBar.jsx';
import { Layers, Plus, Trash2, Save } from 'lucide-react';
import toast from 'react-hot-toast';

const ALL_STATUSES = [
  'NEW','CONTACTED','QUALIFIED','PRESENTATION','FOLLOW_UP','SOLD','NOT_INTERESTED',
  'NO_ANSWER','OPT_OUT','RECYCLED','HOT','NO_SHOW','NO_SALE','BOOKED_APPOINTMENT','DNC','QUOTED','CALL_BACK',
];

export default function LeadStatusGroups() {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ['lsg'], queryFn: () => apiGet('/lead-status-groups') });
  const groups = data?.items || [];

  return (
    <div className="space-y-4">
      <TopBar title="Lead Status Groups" subtitle="Bundle related dispositions for filtering & reporting" />
      <NewGroupForm onCreated={() => qc.invalidateQueries({ queryKey: ['lsg'] })} />
      <div className="grid md:grid-cols-2 gap-3">
        {groups.length === 0 && <div className="panel p-6 text-white/50 text-sm">No groups yet. Create one above.</div>}
        {groups.map((g) => <GroupRow key={g._id} g={g} onChanged={() => qc.invalidateQueries({ queryKey: ['lsg'] })} />)}
      </div>
    </div>
  );
}

function NewGroupForm({ onCreated }) {
  const [name, setName] = useState('');
  const [color, setColor] = useState('#7c3aed');
  const [busy, setBusy] = useState(false);
  async function submit(e) {
    e.preventDefault();
    if (!name.trim()) return toast.error('Name required');
    setBusy(true);
    try { await apiPost('/lead-status-groups', { name: name.trim(), color, statuses: [] }); toast.success('Created'); setName(''); onCreated(); }
    catch (err) { toast.error(err.message); } finally { setBusy(false); }
  }
  return (
    <form onSubmit={submit} className="panel p-4 flex items-end gap-3">
      <div className="flex-1">
        <label className="block text-xs text-white/60 mb-1">Group name</label>
        <input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Hot Pipeline" className="input w-full" />
      </div>
      <div>
        <label className="block text-xs text-white/60 mb-1">Color</label>
        <input type="color" value={color} onChange={(e) => setColor(e.target.value)} className="h-10 w-16 rounded-sm border-white/10 bg-transparent" />
      </div>
      <button disabled={busy} className="btn-primary inline-flex items-center gap-2"><Plus className="w-4 h-4" /> Create group</button>
    </form>
  );
}

function GroupRow({ g, onChanged }) {
  const [name, setName] = useState(g.name);
  const [color, setColor] = useState(g.color || '#7c3aed');
  const [statuses, setStatuses] = useState(g.statuses || []);
  const [dirty, setDirty] = useState(false);
  function toggle(s) { setStatuses((cur) => { const next = cur.includes(s) ? cur.filter((x) => x !== s) : [...cur, s]; setDirty(true); return next; }); }
  async function save() { try { await apiPut(`/lead-status-groups/${g._id}`, { name, color, statuses }); toast.success('Saved'); setDirty(false); onChanged(); } catch (e) { toast.error(e.message); } }
  async function del() { if (!confirm('Delete this group?')) return; await apiDel(`/lead-status-groups/${g._id}`); onChanged(); }
  return (
    <div className="panel p-4 space-y-3">
      <div className="flex items-center gap-2">
        <div className="w-3 h-3 rounded-full" style={{ background: color }} />
        <input value={name} onChange={(e) => { setName(e.target.value); setDirty(true); }} className="input flex-1" />
        <input type="color" value={color} onChange={(e) => { setColor(e.target.value); setDirty(true); }} className="h-9 w-12 rounded-sm bg-transparent border-white/10" />
        <button onClick={del} className="text-white/40 hover:text-rose-400"><Trash2 className="w-4 h-4" /></button>
      </div>
      <div className="flex flex-wrap gap-1">
        {ALL_STATUSES.map((s) => (
          <button key={s} type="button" onClick={() => toggle(s)} className={`px-2 py-1 rounded-sm text-xs border ${statuses.includes(s) ? 'bg-civix-purple/30 border-civix-purple/60 text-white' : 'border-white/10 text-white/50 hover:text-white'}`}>{s}</button>
        ))}
      </div>
      {dirty && <button onClick={save} className="btn-primary w-full inline-flex items-center justify-center gap-2"><Save className="w-4 h-4" /> Save changes</button>}
    </div>
  );
}
